import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ShareLoadButtons } from '../components/SocialButtons';
import { MapPin, Package, Calendar, TrendingUp, Truck, Search, Navigation, Loader2, MessageSquare } from 'lucide-react';
import { formatDateShortAU } from '../utils/dateFormat';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BrowseLoads = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loads, setLoads] = useState([]);
  const [filteredLoads, setFilteredLoads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [locationSearching, setLocationSearching] = useState(false);
  const [radiusSearch, setRadiusSearch] = useState({ active: false, radius: 200, lat: null, lng: null, town: '' });
  const [townSearching, setTownSearching] = useState(false);
  const [stateFilter, setStateFilter] = useState('');
  const [fromState, setFromState] = useState('');
  const [toState, setToState] = useState('');

  const STATES = ['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT'];
  const [filters, setFilters] = useState(() => {
    const saved = sessionStorage.getItem('browseLoadsFilters');
    if (saved) {
      try { return JSON.parse(saved); } catch { /* fall through */ }
    }
    return {
      pickupLocation: '',
      dropoffLocation: '',
      cargoType: searchParams.get('category') || '',
      status: 'pending',
      minWeight: '',
      maxWeight: ''
    };
  });

  const cargoCategories = [
    'All Types',
    'Heavy Haulage',
    'Machinery Transport',
    'Car Transport',
    'Container Transport',
    'General Freight',
    'Palletised Freight',
    'Bulk Transport',
    'Grain Transport',
    'Livestock Transport',
    'Boat Transport',
    'Truck Transport',
    'Trailer Transport'
  ];

  useEffect(() => {
    fetchLoads();
  }, []);

  useEffect(() => {
    applyFilters();
    sessionStorage.setItem('browseLoadsFilters', JSON.stringify(filters));
  }, [filters, loads]);

  const fetchLoads = async () => {
    try {
      const response = await axios.get(`${API}/loads`);
      setLoads(response.data);
      setFilteredLoads(response.data);
      // Restore scroll position after loads render
      setTimeout(() => {
        const savedY = sessionStorage.getItem('browseLoadsScrollY');
        if (savedY) {
          window.scrollTo(0, parseInt(savedY, 10));
          sessionStorage.removeItem('browseLoadsScrollY');
        }
      }, 100);
    } catch (error) {
      toast.error('Error', {
        description: 'Failed to load listings'
      });
    } finally {
      setLoading(false);
    }
  };

  const searchByLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation not supported by your browser');
      return;
    }
    setLocationSearching(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        setRadiusSearch(prev => ({ ...prev, active: true, lat: latitude, lng: longitude, town: 'My Location' }));
        try {
          const response = await axios.get(`${API}/loads/search/radius`, {
            params: { lat: latitude, lng: longitude, radius_km: radiusSearch.radius }
          });
          setFilteredLoads(response.data);
          toast.success(`Found ${response.data.length} loads within ${radiusSearch.radius}km`);
        } catch (error) {
          toast.error('Search failed. Try again.');
        } finally {
          setLocationSearching(false);
        }
      },
      (error) => {
        setLocationSearching(false);
        toast.error('Location access denied. Please enable location services.');
      }
    );
  };

  const searchByTown = async () => {
    if (!radiusSearch.town) {
      toast.error('Enter a town or suburb name');
      return;
    }
    setTownSearching(true);
    try {
      const geoRes = await axios.get(`${API}/geocode?town=${encodeURIComponent(radiusSearch.town)}`);
      const { lat, lng } = geoRes.data;
      setRadiusSearch(prev => ({ ...prev, active: true, lat, lng }));
      setStateFilter(''); // Clear state filter when doing town search
      const response = await axios.get(`${API}/loads/search/radius`, {
        params: { lat, lng, radius_km: radiusSearch.radius }
      });
      setFilteredLoads(response.data);
      toast.success(`Found ${response.data.length} loads within ${radiusSearch.radius}km of ${radiusSearch.town}`);
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('Could not find that location. Try adding a state, e.g. "Adelaide, SA"');
      } else {
        toast.error('Search failed. Try again.');
      }
    } finally {
      setTownSearching(false);
    }
  };

  const searchByState = (from, to) => {
    const f = from !== undefined ? from : fromState;
    const t = to !== undefined ? to : toState;
    setFromState(f);
    setToState(t);
    setRadiusSearch(prev => ({ ...prev, active: false, town: '' }));
    
    if (!f && !t) {
      setStateFilter('');
      applyFilters();
      return;
    }
    
    setStateFilter(f || t || '');
    const filtered = loads.filter(load => {
      const pickup = (load.pickup_location || '').toUpperCase();
      const dropoff = (load.dropoff_location || '').toUpperCase();
      const fromMatch = !f || pickup.includes(f.toUpperCase());
      const toMatch = !t || dropoff.includes(t.toUpperCase());
      return fromMatch && toMatch;
    });
    filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    setFilteredLoads(filtered);
    const label = f && t ? `${f} → ${t}` : f ? `From ${f}` : `To ${t}`;
    toast.success(`Found ${filtered.length} loads ${label}`);
  };

  const clearLocationSearch = () => {
    setRadiusSearch({ active: false, radius: 200, lat: null, lng: null, town: '' });
    setStateFilter('');
    setFromState('');
    setToState('');
    applyFilters();
  };

  const applyFilters = () => {
    let filtered = [...loads];

    if (filters.pickupLocation) {
      filtered = filtered.filter(load =>
        load.pickup_location.toLowerCase().includes(filters.pickupLocation.toLowerCase())
      );
    }

    if (filters.dropoffLocation) {
      filtered = filtered.filter(load =>
        load.dropoff_location.toLowerCase().includes(filters.dropoffLocation.toLowerCase())
      );
    }

    if (filters.cargoType && filters.cargoType !== 'All Types') {
      filtered = filtered.filter(load =>
        load.cargo_type.toLowerCase().includes(filters.cargoType.toLowerCase())
      );
    }

    if (filters.minWeight) {
      filtered = filtered.filter(load =>
        load.weight && load.weight >= parseFloat(filters.minWeight)
      );
    }

    if (filters.maxWeight) {
      filtered = filtered.filter(load =>
        load.weight && load.weight <= parseFloat(filters.maxWeight)
      );
    }

    // Sort by newest first (most recent created_at)
    filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

    setFilteredLoads(filtered);
  };

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const clearFilters = () => {
    sessionStorage.removeItem('browseLoadsFilters');
    setFilters({
      pickupLocation: '',
      dropoffLocation: '',
      cargoType: '',
      status: 'pending',
      minWeight: '',
      maxWeight: ''
    });
  };

  const handleViewLoad = (loadId) => {
    // Save scroll position so we can restore it when user comes back
    sessionStorage.setItem('browseLoadsScrollY', window.scrollY.toString());
    navigate(`/load/${loadId}`);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified';
    return formatDateShortAU(dateString);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-12 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading available loads...</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="bg-[#1e3a5f] text-white py-12">
        <div className="container mx-auto px-4 sm:px-6">
          <h1 className="text-3xl sm:text-4xl font-bold mb-4">Browse Available Loads</h1>
          <p className="text-lg text-gray-200">Find loads matching your route and grow your business</p>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 py-8">
        {/* Location Radius Search */}
        <Card className="p-4 sm:p-6 mb-4" data-testid="radius-search-card">
          <div className="flex items-center gap-2 mb-3">
            <Navigation className="w-5 h-5 text-red-600" />
            <h2 className="text-base sm:text-lg font-semibold text-[#1e3a5f]">Search Loads by Location</h2>
          </div>
          
          {/* State Filter */}
          <div className="mb-4">
            <Label className="text-sm mb-1.5 block">Filter by State</Label>
            <div className="flex flex-col sm:flex-row gap-3 items-end">
              <div className="flex-1">
                <p className="text-xs text-gray-500 mb-1">From (Pickup)</p>
                <select
                  value={fromState}
                  onChange={(e) => searchByState(e.target.value, undefined)}
                  className="w-full h-10 border rounded-md px-3 text-sm"
                  data-testid="from-state"
                >
                  <option value="">Any State</option>
                  {STATES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <span className="text-gray-400 font-bold hidden sm:block pb-2">→</span>
              <div className="flex-1">
                <p className="text-xs text-gray-500 mb-1">To (Dropoff)</p>
                <select
                  value={toState}
                  onChange={(e) => searchByState(undefined, e.target.value)}
                  className="w-full h-10 border rounded-md px-3 text-sm"
                  data-testid="to-state"
                >
                  <option value="">Any State</option>
                  {STATES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* Town + Radius Search */}
          <div className="border-t pt-4">
            <Label className="text-sm mb-1.5 block">Or search by town + radius</Label>
            <div className="flex flex-col sm:flex-row gap-3 items-end">
              <div className="flex-[2]">
                <Input
                  value={radiusSearch.town}
                  onChange={(e) => setRadiusSearch(prev => ({ ...prev, town: e.target.value }))}
                  onKeyDown={(e) => e.key === 'Enter' && searchByTown()}
                  placeholder="e.g. Adelaide or Melbourne"
                  data-testid="town-search-input"
                />
              </div>
              <div className="flex-1">
                <Select
                  value={String(radiusSearch.radius)}
                  onValueChange={(val) => setRadiusSearch(prev => ({ ...prev, radius: Number(val) }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="50">50 km</SelectItem>
                    <SelectItem value="100">100 km</SelectItem>
                    <SelectItem value="200">200 km</SelectItem>
                    <SelectItem value="300">300 km</SelectItem>
                    <SelectItem value="500">500 km</SelectItem>
                    <SelectItem value="1000">1000 km</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button
                onClick={searchByTown}
                disabled={townSearching}
                className="bg-red-600 hover:bg-red-700 text-white w-full sm:w-auto"
                data-testid="search-town-btn"
              >
                {townSearching ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Searching...</>
                ) : (
                  <><Search className="w-4 h-4 mr-2" /> Search</>
                )}
              </Button>
              <Button
                onClick={searchByLocation}
                disabled={locationSearching}
                variant="outline"
                className="w-full sm:w-auto"
                data-testid="use-my-location-btn"
              >
                {locationSearching ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Locating...</>
                ) : (
                  <><Navigation className="w-4 h-4 mr-2" /> My Location</>
                )}
              </Button>
              {(radiusSearch.active || stateFilter) && (
                <Button variant="outline" onClick={clearLocationSearch} className="w-full sm:w-auto" data-testid="clear-location-btn">
                  Clear
                </Button>
              )}
            </div>
          </div>

          {radiusSearch.active && (
            <p className="mt-3 text-sm text-green-700 bg-green-50 p-2 rounded">
              Showing loads within <strong>{radiusSearch.radius}km</strong> of <strong>{radiusSearch.town || 'your location'}</strong>
            </p>
          )}
          {stateFilter && !radiusSearch.active && (
            <p className="mt-3 text-sm text-green-700 bg-green-50 p-2 rounded">
              Showing loads {fromState && toState ? <><strong>{fromState}</strong> → <strong>{toState}</strong></> : fromState ? <>from <strong>{fromState}</strong></> : <>to <strong>{toState}</strong></>}
            </p>
          )}
        </Card>

        {/* Filters Section */}
        <Card className="p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-[#1e3a5f] flex items-center gap-2">
              <Search className="w-5 h-5" />
              Search & Filter Loads
            </h2>
            <Button variant="outline" onClick={clearFilters} size="sm">
              Clear Filters
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <Label>Pickup Location</Label>
              <Input
                placeholder="e.g., Sydney, NSW"
                value={filters.pickupLocation}
                onChange={(e) => handleFilterChange('pickupLocation', e.target.value)}
              />
            </div>

            <div>
              <Label>Dropoff Location</Label>
              <Input
                placeholder="e.g., Melbourne, VIC"
                value={filters.dropoffLocation}
                onChange={(e) => handleFilterChange('dropoffLocation', e.target.value)}
              />
            </div>

            <div>
              <Label>Cargo Type</Label>
              <Select
                value={filters.cargoType}
                onValueChange={(value) => handleFilterChange('cargoType', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  {cargoCategories.map((category) => (
                    <SelectItem key={category} value={category}>
                      {category}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Weight Range (kg)</Label>
              <div className="flex gap-2">
                <Input
                  type="number"
                  placeholder="Min"
                  value={filters.minWeight}
                  onChange={(e) => handleFilterChange('minWeight', e.target.value)}
                  className="w-1/2"
                />
                <Input
                  type="number"
                  placeholder="Max"
                  value={filters.maxWeight}
                  onChange={(e) => handleFilterChange('maxWeight', e.target.value)}
                  className="w-1/2"
                />
              </div>
            </div>
          </div>
        </Card>

        {/* Results Summary */}
        <div className="flex items-center justify-between mb-6">
          <p className="text-gray-600">
            Showing <span className="font-semibold text-[#1e3a5f]">{filteredLoads.length}</span> available loads
          </p>
          <Button onClick={fetchLoads} variant="outline" size="sm">
            Refresh
          </Button>
        </div>

        {/* Loads Grid */}
        {filteredLoads.length === 0 ? (
          <Card className="p-12 text-center">
            <Package className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No loads found</h3>
            <p className="text-gray-600 mb-4">Try adjusting your filters or check back later for new loads</p>
            <Button onClick={clearFilters} className="bg-red-600 hover:bg-red-700 text-white">
              Clear All Filters
            </Button>
          </Card>
        ) : (
          <div className="grid gap-6">
            {filteredLoads.map((load) => (
              <Card key={load.id} className="p-6 hover:shadow-lg transition-shadow">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  {/* Load Details */}
                  <div className="flex-1 space-y-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="text-xl font-bold text-[#1e3a5f] mb-1">
                          {load.cargo_type}
                        </h3>
                        <p className="text-sm text-gray-500">Load ID: {load.id.slice(0, 8)}</p>
                      </div>
                      <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-semibold">
                        {load.status.toUpperCase()}
                      </span>
                      {load.distance_km !== undefined && (
                        <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-semibold">
                          {load.distance_km} km away
                        </span>
                      )}
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="flex items-start gap-2">
                        <MapPin className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-semibold text-gray-700">Route</p>
                          <p className="text-sm text-gray-600">
                            {load.pickup_location} → {load.dropoff_location}
                          </p>
                        </div>
                      </div>

                      {load.weight && (
                        <div className="flex items-start gap-2">
                          <TrendingUp className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="font-semibold text-gray-700">Weight</p>
                            <p className="text-sm text-gray-600">{load.weight} kg</p>
                          </div>
                        </div>
                      )}

                      {load.pickup_date && (
                        <div className="flex items-start gap-2">
                          <Calendar className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="font-semibold text-gray-700">Available From</p>
                            <p className="text-sm text-gray-600">{formatDate(load.pickup_date)}</p>
                          </div>
                        </div>
                      )}

                      {load.dimensions && (
                        <div className="flex items-start gap-2">
                          <Package className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="font-semibold text-gray-700">Dimensions</p>
                            <p className="text-sm text-gray-600">{load.dimensions}</p>
                          </div>
                        </div>
                      )}
                    </div>

                    {load.cargo_description && (
                      <p className="text-sm text-gray-600 line-clamp-2">
                        {load.cargo_description}
                      </p>
                    )}

                    {/* Privacy notice */}
                    <div className="bg-blue-50 p-2 rounded text-xs text-blue-700">
                      <strong>Privacy:</strong> Shipper contact details revealed after quote acceptance. If you require more information, please contact the Truckn Send It team to obtain this for you.
                    </div>
                  </div>

                  {/* Action Button */}
                  <div className="flex flex-col gap-2 lg:items-end">
                    <Button
                      onClick={() => handleViewLoad(load.id)}
                      className="bg-red-600 hover:bg-red-700 text-white w-full lg:w-auto"
                    >
                      View Details & Quote
                    </Button>
                    <div className="flex items-center gap-1.5 text-sm text-gray-600" data-testid={`quotes-count-${load.id}`}>
                      <MessageSquare className="w-4 h-4" />
                      <span>{load.quotes_received || 0} {(load.quotes_received || 0) === 1 ? 'Quote' : 'Quotes'} Received</span>
                    </div>
                    <p className="text-xs text-gray-500 text-center lg:text-right">
                      Posted: {formatDate(load.created_at)}
                    </p>
                    <ShareLoadButtons load={load} />
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Info Card */}
        <Card className="mt-8 p-6 bg-blue-50">
          <div className="flex items-start gap-3">
            <Truck className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-semibold text-gray-800 mb-2">New to Truckn Send It?</h3>
              <p className="text-sm text-gray-700 mb-3">
                Sign up as a carrier to access thousands of loads, get secure payments, and grow your business.
              </p>
              <Button
                onClick={() => navigate('/signup?role=carrier')}
                variant="outline"
                className="border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white"
              >
                Join as a Carrier
              </Button>
            </div>
          </div>
        </Card>
      </div>
      
      <Footer />
    </div>
  );
};

export default BrowseLoads;
